import { useClient } from "context/auth-context";
import { useToast } from "context/toast-context";
import { AuthorizationResult } from "domain/auth";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { client } from "utils/api-client";
import { isEmailValid } from "utils/validation-utils";

const clientId = window.APP_CONFIG.AUTH0_CLIENTID;

export const useAuthMethod = (email: string | undefined) => {
  const [method, setMethod] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getMethod = useCallback(async () => {
    if (!email) return;
    const isValid = isEmailValid(email);
    if (!isValid) return;
    setIsLoading(true);
    try {
      const method = await client<{ authFlow: string }>("auth/flow", {
        method: "POST",
        data: { email }
      });
      setMethod(method.authFlow);
      setIsLoading(false);
    } catch (err) {
      setMethod(undefined);
      setIsLoading(false);
      console.error(err);
    }
  }, [email]);

  useEffect(() => {
    getMethod();
  }, [getMethod]);

  return { method, isLoading };
};

export type CodePayload = EmailCodePayload | PhoneCodePayload;

type EmailCodePayload = {
  email: string;
};

type PhoneCodePayload = {
  phoneNumber: string;
};

export const useSendCode = (successCallback?: () => void, errorCallback?: () => void) => {
  const mutation = useMutation<any, Error, CodePayload>(
    payload => {
      return client(`auth/code?$checkExists=true`, {
        method: "POST",
        data: {
          ...payload,
          clientId,
          flowVersion: 2
        }
      });
    },
    {
      onSuccess: () => {
        successCallback && successCallback();
      },
      onError: () => {
        errorCallback && errorCallback();
      }
    }
  );

  return mutation;
};

type ValidateCodePayload = {
  email?: string;
  phoneNumber?: string;
  code: string;
};

export const useValidateCode = (
  callback: (result: AuthorizationResult) => void,
  errorCallback?: (message: string) => void
) => {
  const mutation = useMutation<any, Error, ValidateCodePayload>(
    payload => {
      return client(`auth/token`, {
        method: "POST",
        data: {
          ...payload,
          clientId,
          flowVersion: 2
        }
      });
    },
    {
      onSuccess: (result: AuthorizationResult, variables) => {
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("clientId");
        localStorage.removeItem("refreshToken");

        if (result.isAdditionalAuthorizationRequired) localStorage.setItem("tempToken", result.tokenData.access_token);

        if (!result.isAdditionalAuthorizationRequired) {
          if (result.tokenData.client_id) {
            localStorage.setItem("clientId", result.tokenData.client_id);
          }
          localStorage.setItem("refreshToken", result.tokenData.refresh_token);
        }

        callback(result);
      },
      onError: err => {
        if (errorCallback) errorCallback(err.message);
      }
    }
  );

  return mutation;
};

export interface Invitation {
  id: string;
  organisationUserId: string;
  organisationName: string;
  firstName: string;
  phoneNumber?: string;
  lastName: string;
  email: string;
  inviteUrl: string;
  tenantId: string;
}

export const useInvitation = (inviteKey: string) => {
  const client = useClient<Invitation>();
  const { data: invite, isLoading } = useQuery<Invitation | undefined, Error>({
    queryKey: [`invitation`, inviteKey],
    queryFn: async () => {
      try {
        return await client(`activeinvitations/${inviteKey}`);
      } catch (err: any) {
        window.location.href = "/";
      }
    }
  });

  return { invite, isLoading };
};

export const useAcceptInvitation = (successCallBack: () => void) => {
  const client = useClient();
  const mutation = useMutation<unknown, Error, { inviteKey: string }>(
    updates =>
      client(`activeinvitations/${updates.inviteKey}/accept`, {
        method: "PUT"
      }),
    {
      onSuccess: () => {
        successCallBack && successCallBack();
      }
    }
  );

  return mutation;
};

export interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export const useSignup = (successCallBack: () => void) => {
  const client = useClient();
  const mutation = useMutation<unknown, Error, { inviteKey: string; details: SignupData }>(
    data =>
      client(`activeinvitations/${data.inviteKey}/signup`, {
        method: "POST",
        data: data.details
      }),
    {
      onSuccess: () => {
        successCallBack && successCallBack();
      }
    }
  );

  return mutation;
};

interface ChangePasswordParams {
  callback?: () => void;
}

export const useChangePassword = ({ callback }: ChangePasswordParams) => {
  const client = useClient();
  const { showSnackBar } = useToast();
  const mutation = useMutation<unknown, Error, { password: string; oldPassword: string }>(
    data =>
      client(`auth/password`, {
        method: "PATCH",
        data
      }),
    {
      onSuccess: () => {
        showSnackBar && showSnackBar(`passwordChangedSuccessfully`);
        callback && callback();
      },
      onError: () => {
        showSnackBar && showSnackBar(`failedToChangePassword`, undefined, "error");
      }
    }
  );

  return mutation;
};

interface SetPasswordParams {
  callback?: () => void;
}

export const useSetPassword = ({ callback }: SetPasswordParams) => {
  const client = useClient();
  const { showSnackBar } = useToast();
  const mutation = useMutation<unknown, Error, { password: string }>(
    data =>
      client(`auth/password`, {
        method: "POST",
        data
      }),
    {
      onSuccess: () => {
        showSnackBar && showSnackBar(`passwordSetSuccessfully`);
        callback && callback();
      },
      onError: () => {
        showSnackBar && showSnackBar(`failedToSetPassword`, undefined, "error");
      }
    }
  );

  return mutation;
};

export const useRedirectAfterAuthUrl = () => {
  return useLocalStorage("app-redirect-after-auth-url", "");
};
